<template>
  <div id="app">
    <!-- Loading Spinner -->
    <LoadingSpinner v-if="loading" />

    <!-- Separate the sidebar components to prevent recursive updates -->
    <NavBar 
      v-if="!isDashboardRoute && !loading" 
      :shoppingBagItems="shoppingBagItems" 
      @update-shopping-bag="updateShoppingBag" 
    />
    <DashboardSidebar 
      v-if="isDashboardRoute && !loading" 
    />
    
    <router-view 
      @add-to-shopping-bag="addToShoppingBag" 
      @page-loading="handlePageLoading" 
      v-show="!loading" 
    />

  <!-- Conditional rendering of Footer based on the current route -->
  <AppFooter v-if="!isDashboardRoute && !loading" />

    <!-- Scroll to Top Button -->
    <button v-if="showScrollToTop" @click="scrollToTop" class="scroll-to-top">↑</button>
  </div>
</template>

<script>
import NavBar from './components/Frontend Nav Components/NavBar.vue';
import DashboardSidebar from './components/Dashboard Nav Components/DashboardSidebar.vue';
import Footer from './components/Frontend Nav Components/FooterBar.vue';
import LoadingSpinner from './components/LoadingSpinner.vue';
import cartService from './services/cartService.js';


export default {
  name: 'App',
  components: {
    NavBar,
    DashboardSidebar,
    AppFooter: Footer,
    LoadingSpinner
  },
  computed: {
    isDashboardRoute() {
      return this.$route.path.startsWith('/dashboard');
    },
    isBlackBackgroundRoute() {
      const blackBackgroundRoutes = ['/', '/gift-cards', '/incentives'];
      return blackBackgroundRoutes.includes(this.$route.path);
    }
  },
  data() {
    return {
      shoppingBagItems: JSON.parse(localStorage.getItem('shoppingBagItems')) || [],
      loading: true,
      tawkToScript: null,
      showScrollToTop: false
    };
  },
  methods: {
    addToShoppingBag(item) {
      const shoppingBagItems = [...this.shoppingBagItems, item];
      this.shoppingBagItems = shoppingBagItems;
      localStorage.setItem('shoppingBagItems', JSON.stringify(shoppingBagItems));
    },
    updateShoppingBag(items) {
      this.shoppingBagItems = items;
      localStorage.setItem('shoppingBagItems', JSON.stringify(items));
    },
    initializeTawkTo() {
      if (!this.isDashboardRoute && !document.getElementById('tawkto-script')) {
        var s1 = document.createElement("script");
        s1.async = true;
        s1.src = 'https://embed.tawk.to/672a11024304e3196add7415/1ibu54s5q';
        s1.charset = 'UTF-8';
        s1.setAttribute('crossorigin', '*');
        s1.id = 'tawkto-script';
        document.head.appendChild(s1);
        this.tawkToScript = s1;
      }
    },
    removeTawkTo() {
      // Remove the script tag
      if (this.tawkToScript) {
        this.tawkToScript.remove();
        this.tawkToScript = null;
      }
      
      // Remove the Tawk_API elements
      const tawkToElements = document.querySelectorAll('iframe[title*="chat"]');
      tawkToElements.forEach(element => element.remove());
      
      // Clean up the global Tawk_API object
      if (window.Tawk_API) {
        if (typeof window.Tawk_API.hideWidget === 'function') {
          window.Tawk_API.hideWidget();
        }
        // Remove the global variables
        delete window.Tawk_API;
        delete window.Tawk_LoadStart;
      }
    },
    handleRouteChange() {
      if (this.isDashboardRoute) {
        this.removeTawkTo();
      } else {
        this.initializeTawkTo();
      }
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    handleScroll() {
      this.showScrollToTop = window.scrollY > 200;
    },
    handlePageLoading(isLoading) {
      // When a page component signals it's loading, update the app's loading state
      this.loading = isLoading;
    },
    initializeCartSync() {
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      
      if (token && user) {
        // User is logged in, start cart synchronization
        cartService.startAutoSync();
        console.log('Cart synchronization started');
      }
    }
  },
  watch: {
    shoppingBagItems(newItems) {
      this.$emit('update-shopping-bag', newItems);
    },
    '$route': {
      handler() {
        this.handleRouteChange();
        // Update body background color on route change
        document.body.style.setProperty('--body-bg-color', this.isBlackBackgroundRoute ? 'black' : 'white');
      },
      immediate: true
    }
  },
  mounted() {
    // Only set location from hostname if no location is already saved in localStorage
    const savedLocation = localStorage.getItem('selectedLocation');
    if (!savedLocation) {
      const currentHostname = window.location.hostname;
      if(currentHostname.includes('summitshopper.com')){
        localStorage.setItem('selectedLocation', 'Summit County, Ohio');
      } else if(currentHostname.includes('starkshopper.com')){
        localStorage.setItem('selectedLocation', 'Stark County, Ohio');
      }
    }

    // Handle referral code from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const referralCode = urlParams.get('code');
    if (referralCode) {
      localStorage.setItem('referralCode', referralCode);
      console.log('Referral code detected and stored:', referralCode);
    }

    // Set initial loading to true
    this.loading = true;
    
    // Add a default timeout to hide the spinner if no page-loading event is received
    setTimeout(() => {
      this.loading = false;
    }, 1000);

    // Initialize Tawk.to only if not on dashboard route
    if (!this.isDashboardRoute) {
      this.initializeTawkTo();
    }

    // Set body background color based on route
    document.body.style.setProperty('--body-bg-color', this.isBlackBackgroundRoute ? 'black' : 'white');

    window.addEventListener('scroll', this.handleScroll);

    // Initialize cart synchronization if user is logged in
    this.initializeCartSync();
  },
  beforeUnmount() {
    // Cleanup Tawk.to when component is destroyed
    this.removeTawkTo();
    window.removeEventListener('scroll', this.handleScroll);
    
    // Stop cart synchronization
    cartService.stopAutoSync();
  }
};
</script>

<style>
html, body {
  margin: 0;
  padding: 0;
  font-family: 'Roboto', sans-serif;
}

body {
  background-color: var(--body-bg-color);
}

#app {
  width: 100%;
  height: 100vh;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  font-family: 'Roboto', sans-serif;
}

.grecaptcha-badge { 
    visibility: hidden !important;
}

.scroll-to-top {
  position: fixed;
  bottom: 20px;
  left: 20px;
  background-color: #4C6B30;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1000;
}
</style>